import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatINR } from "@/lib/api";
import RequireCompany from "@/components/RequireCompany";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer } from "lucide-react";
import { devError } from "@/lib/logger";

function monthName(ym) {
  if (!ym) return "—";
  return new Date(`${ym}-01`).toLocaleString("en-IN", { month: "long", year: "numeric" });
}

function Inner() {
  const { psid } = useParams();
  const navigate = useNavigate();
  const [slip, setSlip] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get(`/payslips/${psid}`)
      .then((r) => { if (mounted) setSlip(r.data); })
      .catch((err) => devError("Payslip load failed", err));
    return () => { mounted = false; };
  }, [psid]);

  if (!slip) return <div className="p-6 text-sm text-gray-500" data-testid="payslip-loading">Loading…</div>;

  const earningsRows = [
    ["Basic", slip.earnings.basic],
    ["HRA", slip.earnings.hra],
    ["Allowance", slip.earnings.allowance],
    ["Bonus", slip.earnings.bonus],
    ["Other", slip.earnings.other],
  ].filter(([, v]) => v > 0);

  const deductionsRows = [
    ["Provident Fund (12%)", slip.deductions.pf],
    ["ESI (0.75%)", slip.deductions.esi],
    ["Professional Tax", slip.deductions.pt],
    ["TDS", slip.deductions.tds],
    ["Other", slip.deductions.other],
  ].filter(([, v]) => v > 0);

  return (
    <>
      <div className="flex items-center justify-between p-4 border-b print:hidden">
        <Button variant="outline" onClick={() => navigate(-1)} className="h-8" data-testid="btn-back-payslips"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</Button>
        <Button onClick={() => window.print()} className="h-8" data-testid="btn-print-payslip"><Printer className="w-4 h-4 mr-1.5" /> Print / Save as PDF</Button>
      </div>
      <div className="p-6 flex justify-center" data-testid="payslip-print-page">
        <div className="payslip" id="payslip-print-area">
          {/* Header */}
          <div className="text-center pb-3 border-b border-gray-300">
            <div className="text-lg font-bold text-gray-900" data-testid="payslip-company-name">{slip.company?.name || "Company"}</div>
            {slip.company?.address && <div className="text-[11px] text-gray-600">{slip.company.address}</div>}
            {slip.company?.gstin && <div className="text-[11px] text-gray-600">GSTIN: <span className="font-mono">{slip.company.gstin}</span></div>}
            <div className="text-sm font-semibold text-gray-800 mt-2" data-testid="payslip-title">Payslip for {monthName(slip.month)}</div>
          </div>

          {/* Employee meta */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 py-3 text-[11px] border-b border-gray-200">
            <div><span className="text-gray-500">Employee Code:</span> <span className="font-mono font-semibold ml-2">{slip.employee_code}</span></div>
            <div><span className="text-gray-500">PAN:</span> <span className="font-mono ml-2">{slip.pan || "—"}</span></div>
            <div><span className="text-gray-500">Name:</span> <span className="font-semibold ml-2">{slip.employee_name}</span></div>
            <div><span className="text-gray-500">PF UAN:</span> <span className="font-mono ml-2">{slip.pf_uan || "—"}</span></div>
            <div><span className="text-gray-500">Designation:</span> <span className="ml-2">{slip.designation || "—"}</span></div>
            <div><span className="text-gray-500">ESI No.:</span> <span className="font-mono ml-2">{slip.esi_no || "—"}</span></div>
            <div><span className="text-gray-500">Days Worked:</span> <span className="font-mono ml-2">{slip.days_in_month - slip.lop_days} / {slip.days_in_month}</span></div>
            <div><span className="text-gray-500">LOP Days:</span> <span className="font-mono ml-2">{slip.lop_days}</span></div>
          </div>

          {/* Earnings + Deductions tables */}
          <div className="grid grid-cols-2 gap-6 mt-4">
            <div>
              <div className="text-xs font-semibold text-gray-900 mb-1 pb-1 border-b border-gray-300">Earnings</div>
              <table className="w-full text-[11px]">
                <tbody data-testid="payslip-earnings">
                  {earningsRows.map(([k, v]) => (
                    <tr key={k}><td className="py-1 text-gray-700">{k}</td><td className="py-1 text-right font-mono tabular-nums">{formatINR(v)}</td></tr>
                  ))}
                  <tr className="border-t border-gray-300 font-semibold"><td className="py-1.5">Gross Earnings</td><td className="py-1.5 text-right font-mono tabular-nums" data-testid="payslip-gross">{formatINR(slip.gross)}</td></tr>
                </tbody>
              </table>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-900 mb-1 pb-1 border-b border-gray-300">Deductions</div>
              <table className="w-full text-[11px]">
                <tbody data-testid="payslip-deductions">
                  {deductionsRows.length === 0 ? (
                    <tr><td className="py-1 text-gray-400 italic">No deductions</td><td className="py-1 text-right">—</td></tr>
                  ) : deductionsRows.map(([k, v]) => (
                    <tr key={k}><td className="py-1 text-gray-700">{k}</td><td className="py-1 text-right font-mono tabular-nums">{formatINR(v)}</td></tr>
                  ))}
                  <tr className="border-t border-gray-300 font-semibold"><td className="py-1.5">Total Deductions</td><td className="py-1.5 text-right font-mono tabular-nums" data-testid="payslip-deductions-total">{formatINR(slip.total_deductions)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Net pay */}
          <div className="mt-5 p-3 border-2 border-emerald-600 rounded bg-emerald-50 flex items-baseline justify-between">
            <span className="font-semibold text-emerald-900">Net Pay</span>
            <span className="font-mono text-xl font-bold text-emerald-900 tabular-nums" data-testid="payslip-net">₹ {formatINR(slip.net_pay)}</span>
          </div>
          <div className="text-[11px] text-gray-700 mt-1 italic" data-testid="payslip-net-words">{slip.amount_in_words}</div>

          {/* Employer contributions */}
          {(slip.employer_contrib.epf > 0 || slip.employer_contrib.esi > 0) && (
            <div className="mt-4 text-[10px] text-gray-500">
              <div className="font-semibold text-gray-700 mb-1">Employer Contribution (not included in Net Pay):</div>
              EPF (3.67%): ₹ {formatINR(slip.employer_contrib.epf)}  •  EPS (8.33%): ₹ {formatINR(slip.employer_contrib.eps)}  •  ESI (3.25%): ₹ {formatINR(slip.employer_contrib.esi)}
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 grid grid-cols-2 gap-6 text-[10px] text-gray-600">
            <div>This is a computer-generated payslip and does not require a signature.</div>
            <div className="text-right">
              <div className="border-t border-gray-400 pt-1 inline-block w-44">Authorised Signatory</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .payslip {
          width: 210mm;
          min-height: 200mm;
          padding: 12mm;
          background: #ffffff;
          color: #0a0a0a;
          font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto;
          border: 1px solid #e5e7eb;
        }
        @media print {
          @page { size: A4; margin: 10mm; }
          body { background: white; }
          body * { visibility: hidden !important; }
          #payslip-print-area, #payslip-print-area * { visibility: visible !important; }
          #payslip-print-area {
            position: fixed !important;
            top: 0; left: 0;
            width: 100% !important;
            border: none !important;
          }
        }
      `}</style>
    </>
  );
}

export default function PayslipPrint() {
  return <RequireCompany><Inner /></RequireCompany>;
}
